/**
 * StudentSchoolCard — the "My school" card on /settings/account, rendered
 * only for signed-in STUDENTs. One row per school enrollment: a Grade
 * select sourced from the school's configured grade list and a Homeroom
 * select sourced from the school's homeroom list (each renders only when
 * the school has values configured). Selects save immediately (they're
 * discrete choices), matching the surrounding settings cards'
 * save-on-change feel.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { FormField } from '@ordo/ui/form-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { useMemo } from 'react'

import { type MeSchoolEntry, useMeSchoolQuery, useUpdateMeSchoolMutation } from '@/api/me-school'

function SchoolRow({ school }: { school: MeSchoolEntry }) {
  const updateMutation = useUpdateMeSchoolMutation()

  // A legacy value outside the configured list stays visible/pickable.
  const gradeOptions = useMemo(() => {
    const configured = school.gradeOptions
    if (school.gradeLevel && !configured.some((grade) => grade.toUpperCase() === school.gradeLevel!.toUpperCase())) {
      return [school.gradeLevel, ...configured]
    }
    return configured
  }, [school.gradeLevel, school.gradeOptions])
  const homeroomOptions = useMemo(() => {
    const configured = school.homeroomOptions
    if (school.homeroom && !configured.some((value) => value.toUpperCase() === school.homeroom!.toUpperCase())) {
      return [school.homeroom, ...configured]
    }
    return configured
  }, [school.homeroom, school.homeroomOptions])

  // A stored value keeps its option list non-empty (the prepend above), so the
  // length test alone still showed a picker for a fact the campus marks HIDDEN
  // — the server had already emptied its options for exactly that reason
  // (PROD-1229). `hiddenFacts` is the explicit signal; absent on a response
  // cached before PROD-1231, where the length test alone stays in charge.
  const collects = (fact: string) => !school.hiddenFacts?.includes(fact)
  const hasGrades = school.termId !== null && gradeOptions.length > 0 && collects('GRADE')
  const hasHomerooms = school.termId !== null && homeroomOptions.length > 0 && collects('HOMEROOM')

  const save = (input: { gradeLevel?: string; homeroom?: string | null }) => {
    updateMutation.mutate(
      { organizationId: school.organizationId, schoolLocationId: school.schoolLocationId, ...input },
      {
        onSuccess: () => toast.success(`Saved for ${school.schoolLocationName}`),
        onError: (err) =>
          toast.error("Couldn't save", { description: err instanceof Error ? err.message : 'Please try again.' }),
      },
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField label="School">
        <p className="flex h-9 items-center text-sm text-foreground">{school.schoolLocationName}</p>
      </FormField>
      {!hasGrades && !hasHomerooms ? (
        <FormField label="Grade">
          <p className="flex h-9 items-center text-sm text-muted-foreground">
            Your school hasn&apos;t set up grades or homerooms yet.
          </p>
        </FormField>
      ) : null}
      {hasGrades ? (
        <FormField label="Grade" htmlFor={`me-school-grade-${school.schoolLocationId}`}>
          <Select
            value={school.gradeLevel ?? ''}
            disabled={updateMutation.isPending}
            onValueChange={(value) => {
              if (value && value !== school.gradeLevel) {
                save({ gradeLevel: value })
              }
            }}>
            <SelectTrigger id={`me-school-grade-${school.schoolLocationId}`}>
              <SelectValue placeholder="Pick your grade" />
            </SelectTrigger>
            <SelectContent>
              {gradeOptions.map((grade) => (
                <SelectItem key={grade} value={grade}>
                  {grade}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      ) : null}
      {hasHomerooms ? (
        <FormField label="Homeroom" htmlFor={`me-school-homeroom-${school.schoolLocationId}`}>
          <Select
            value={school.homeroom ?? '__none__'}
            disabled={updateMutation.isPending}
            onValueChange={(value) => {
              const next = value === '__none__' ? null : value
              if (next !== school.homeroom) {
                save({ homeroom: next })
              }
            }}>
            <SelectTrigger id={`me-school-homeroom-${school.schoolLocationId}`}>
              <SelectValue placeholder="Pick your homeroom" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not set</SelectItem>
              {homeroomOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      ) : null}
    </div>
  )
}

export function StudentSchoolCard() {
  const { data, isPending, error } = useMeSchoolQuery()

  if (isPending) {
    return <Skeleton className="h-40 w-full" />
  }
  if (error || !data || data.schools.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>My school</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {data.schools.map((school) => (
          <SchoolRow key={`${school.organizationId}:${school.schoolLocationId}`} school={school} />
        ))}
      </CardContent>
    </Card>
  )
}
